/**
 * Shared stats calculation module based on David's rules (2026-10-07):
 * 
 * Stat    | Meaning
 * --------|-------------------------------------------
 * W / L   | Match wins / losses
 * GW / GL | Game wins / losses (today's W/L)
 * MVP     | Winner of a rotation match (highest win % in the session); badge of honour only
 * Win %   | Match win % = W / (W + L)
 * 
 * Event & Overall order (MVPs never affect ranking):
 * 1. Match wins (W)
 * 2. Match win % (W / (W + L))
 * 3. Game difference (GW - GL)
 */

import { calculateHeadToHeadWins, calculateMVP } from './gameLogic.js';

const toPlayerName = (p) => {
  if (!p) return '';
  if (typeof p === 'object') return p.name || p.id || p.uid || '';
  return String(p).trim();
};

const toPlayerArray = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) return val.map(toPlayerName).filter(Boolean);
  if (typeof val === 'object') {
    const vals = Object.values(val);
    if (vals.length > 0) return vals.map(toPlayerName).filter(Boolean);
  }
  if (typeof val === 'string') {
    const s = val.trim();
    return s && s.toLowerCase() !== 'tie' ? [s] : [];
  }
  return [];
};

/** Determine if a session is ranked. Ranked unless explicitly false. */
export function isRankedSession(session) {
  if (!session) return false;
  return session.ranked !== false;
}

/** Determine if session is in Classic mode format. */
export function isClassicSession(session) {
  if (!session) return false;
  return (
    session.sessionMode === 'classic' ||
    Boolean(session.classicMatch) ||
    Boolean(session.matchSummary?.classicMatch) ||
    Boolean(session.sessionHistory?.[0]?.classicMatch)
  );
}

/** Determine if session is in Rotation (MVP) mode. */
export function isRotationSession(session) {
  return !isClassicSession(session);
}

/**
 * Determine if a classic match is complete with a declared winner.
 * Incomplete/early-ended matches award no W/L.
 */
export function isCompletedClassicMatch(session) {
  if (!session || !isClassicSession(session)) return false;
  const firstEntry = session.sessionHistory?.[0];
  const isUnfinished =
    Boolean(session.incomplete) ||
    Boolean(session.unfinished) ||
    Boolean(firstEntry?.incomplete) ||
    Boolean(firstEntry?.unfinished);

  if (isUnfinished) return false;

  const [winners, losers] = getClassicTeams(session);
  return winners.length > 0 && losers.length > 0;
}

/**
 * Extract winning and losing teams from a classic match.
 * Returns [winningPlayers, losingPlayers].
 */
export function getClassicTeams(session) {
  if (!session) return [[], []];
  const matchSummary = session.matchSummary || {};
  const firstEntry = session.sessionHistory?.[0] || {};

  // Check explicit winningTeam / losingTeam declarations
  const winCandidates = [
    toPlayerArray(matchSummary.winningTeam),
    toPlayerArray(session.winningTeam),
    toPlayerArray(firstEntry.winningTeam),
  ];
  const winningTeam = winCandidates.find((arr) => arr.length > 0) || [];

  const loseCandidates = [
    toPlayerArray(matchSummary.losingTeam),
    toPlayerArray(session.losingTeam),
    toPlayerArray(firstEntry.losingTeam),
  ];
  const losingTeam = loseCandidates.find((arr) => arr.length > 0) || [];

  if (winningTeam.length > 0 && losingTeam.length > 0) {
    return [winningTeam, losingTeam];
  }

  // If not explicitly set, determine from sets / setScores
  const team1Candidates = [
    toPlayerArray(matchSummary.team1),
    toPlayerArray(session.team1),
    toPlayerArray(firstEntry.team1),
    toPlayerArray(session.sessionPlayers).slice(0, 2),
  ];
  const team1 = team1Candidates.find((arr) => arr.length > 0) || [];

  const team2Candidates = [
    toPlayerArray(matchSummary.team2),
    toPlayerArray(session.team2),
    toPlayerArray(firstEntry.team2),
    toPlayerArray(session.sessionPlayers).slice(2, 4),
  ];
  const team2 = team2Candidates.find((arr) => arr.length > 0) || [];

  const sets = session.sets || matchSummary.sets || firstEntry.sets || [0, 0];
  const bestOfSets = session.bestOfSets || matchSummary.bestOfSets || 3;
  const setsToWin = Math.ceil(bestOfSets / 2);

  if (sets[0] >= setsToWin) {
    return [team1, team2];
  }
  if (sets[1] >= setsToWin) {
    return [team2, team1];
  }

  return [winningTeam, losingTeam];
}

/**
 * Find tied leaders in a rotation session using the same tie-break rules as calculateMVP:
 * 1. Win percentage
 * 2. Total wins
 * 3. Head-to-head wins among tied players
 */
export function findRotationTiedLeaders(sessionStats = {}, sessionHistory = []) {
  const playersWithStats = [];
  Object.entries(sessionStats || {}).forEach(([player, stats]) => {
    const name = toPlayerName(player);
    if (!name || name.toLowerCase() === 'tie') return;
    const wins = Number(stats?.wins) || 0;
    const losses = Number(stats?.losses) || 0;
    const total = wins + losses;
    if (total > 0) {
      playersWithStats.push({
        player: name,
        winPercentage: wins / total,
        wins,
      });
    }
  });

  if (playersWithStats.length === 0) return [];

  playersWithStats.sort((a, b) => {
    if (b.winPercentage !== a.winPercentage) {
      return b.winPercentage - a.winPercentage;
    }
    return b.wins - a.wins;
  });

  const topWinPercentage = playersWithStats[0].winPercentage;
  const tiedByPct = playersWithStats.filter((p) => p.winPercentage === topWinPercentage);
  const topWins = tiedByPct[0].wins;
  const tiedPlayers = tiedByPct.filter((p) => p.wins === topWins);

  if (tiedPlayers.length === 1) {
    return [tiedPlayers[0].player];
  }

  if (sessionHistory && sessionHistory.length > 0) {
    const h2hResults = tiedPlayers.map((tp) => {
      let totalH2HWins = 0;
      tiedPlayers.forEach((other) => {
        if (tp.player !== other.player) {
          totalH2HWins += calculateHeadToHeadWins(tp.player, other.player, sessionHistory);
        }
      });
      return { player: tp.player, headToHeadWins: totalH2HWins };
    });

    h2hResults.sort((a, b) => b.headToHeadWins - a.headToHeadWins);
    const topH2H = h2hResults[0].headToHeadWins;
    const stillTied = h2hResults.filter((p) => p.headToHeadWins === topH2H);
    return stillTied.map((p) => p.player);
  }

  return tiedPlayers.map((p) => p.player);
}

/**
 * Determine match outcome for a rotation session:
 * Returns: { winners: string[], losers: string[], mvpPlayer: string | null }
 */
export function getRotationMatchOutcome(session) {
  const sessionPlayers = toPlayerArray(session.sessionPlayers);
  const sessionStats = session.sessionStats || {};
  const statsPlayers = Object.keys(sessionStats).map(toPlayerName).filter(Boolean);
  const allSessionPlayers = Array.from(new Set([...sessionPlayers, ...statsPlayers])).filter(
    (n) => n.toLowerCase() !== 'tie'
  );

  let winners = [];
  let mvpPlayer = null;

  const rawMvp = session.mvp;

  if (rawMvp && rawMvp !== 'Tie') {
    if (Array.isArray(rawMvp)) {
      if (rawMvp.length === 1) {
        const name = toPlayerName(rawMvp[0]);
        winners = [name];
        mvpPlayer = name;
      } else {
        winners = rawMvp.map(toPlayerName).filter(Boolean);
        mvpPlayer = null; // Tied leaders, no MVP awarded
      }
    } else {
      const name = toPlayerName(rawMvp);
      winners = [name];
      mvpPlayer = name;
    }
  } else if (rawMvp === 'Tie') {
    winners = findRotationTiedLeaders(sessionStats, session.sessionHistory || []);
    mvpPlayer = null;
  } else {
    // If mvp field was not set, derive from calculateMVP
    const derivedMvp = calculateMVP(sessionStats, session.sessionHistory || []);
    if (derivedMvp && derivedMvp !== 'Tie') {
      const name = toPlayerName(derivedMvp);
      winners = [name];
      mvpPlayer = name;
    } else if (derivedMvp === 'Tie') {
      winners = findRotationTiedLeaders(sessionStats, session.sessionHistory || []);
      mvpPlayer = null;
    }
  }

  const losers = allSessionPlayers.filter((p) => !winners.includes(p));
  return { winners, losers, mvpPlayer };
}

/**
 * Extract per-player game wins (gw) and game losses (gl) from a session.
 * Unchanged from today's per-game wins/losses in both modes.
 */
export function getSessionGameStats(session) {
  const result = {};
  const addGames = (player, won, lost) => {
    const name = toPlayerName(player);
    if (!name || name.toLowerCase() === 'tie') return;
    if (!result[name]) result[name] = { gw: 0, gl: 0 };
    result[name].gw += won;
    result[name].gl += lost;
  };

  const isClassic = isClassicSession(session);

  if (isClassic) {
    // 1. Classic with classicGameHistory
    if (session.classicGameHistory && session.classicGameHistory.length > 0) {
      session.classicGameHistory.forEach((g) => {
        (g.winningTeam || []).forEach((p) => addGames(p, 1, 0));
        (g.losingTeam || []).forEach((p) => addGames(p, 0, 1));
      });
      return result;
    }

    // 2. Classic with sessionHistory containing per-game winningTeam/losingTeam
    const detailedHistory = session.sessionHistory || [];
    const gamesList = detailedHistory.filter((g) => g && g.winningTeam && g.losingTeam && g.scores);
    if (gamesList.length > 0) {
      gamesList.forEach((g) => {
        (g.winningTeam || []).forEach((p) => addGames(p, 1, 0));
        (g.losingTeam || []).forEach((p) => addGames(p, 0, 1));
      });
      return result;
    }

    // 3. Classic with sessionStats
    if (session.sessionStats && Object.keys(session.sessionStats).length > 0) {
      Object.entries(session.sessionStats).forEach(([p, st]) => {
        addGames(p, Number(st?.wins) || 0, Number(st?.losses) || 0);
      });
      return result;
    }

    // 4. Fallback: from setScores
    const [team1, team2] = [
      toPlayerArray(session.team1 || session.matchSummary?.team1 || session.sessionPlayers?.slice(0, 2)),
      toPlayerArray(session.team2 || session.matchSummary?.team2 || session.sessionPlayers?.slice(2, 4)),
    ];
    const rawScores = session.setScores || session.matchSummary?.setScores || [];
    let t1Total = 0;
    let t2Total = 0;
    rawScores.forEach((s) => {
      if (Array.isArray(s)) {
        t1Total += s[0] || 0;
        t2Total += s[1] || 0;
      } else if (s && typeof s === 'object') {
        t1Total += s.team1 ?? s[0] ?? 0;
        t2Total += s.team2 ?? s[1] ?? 0;
      }
    });
    team1.forEach((p) => addGames(p, t1Total, t2Total));
    team2.forEach((p) => addGames(p, t2Total, t1Total));
    return result;
  }

  // Rotation session
  if (session.sessionStats && Object.keys(session.sessionStats).length > 0) {
    Object.entries(session.sessionStats).forEach(([p, st]) => {
      addGames(p, Number(st?.wins) || 0, Number(st?.losses) || 0);
    });
    return result;
  }

  // Fallback from sessionHistory games
  if (session.sessionHistory && session.sessionHistory.length > 0) {
    session.sessionHistory.forEach((g) => {
      (g.winningTeam || []).forEach((p) => addGames(p, 1, 0));
      (g.losingTeam || []).forEach((p) => addGames(p, 0, 1));
    });
  }

  return result;
}

/**
 * Pure function: Calculate per-player match stats from archived sessions.
 * 
 * @param {Array} archivedSessions - List of session documents
 * @param {Object|Function} [filter] - Optional filter function or { eventId, sessionIds, mode }
 * @param {Array} [allPlayers] - Optional initial players list
 * @returns {Object} Map of playerName -> { w, l, gw, gl, mvp, winPct, gameDiff, wins, losses, matchesWon, matchesLost, gamesWon, gamesLost, gamesPlayed, mvpCount }
 */
export function calculateMatchStats(archivedSessions = [], filter = null, allPlayers = []) {
  const statsMap = {};

  const ensurePlayer = (playerName) => {
    const name = toPlayerName(playerName);
    if (!name || name.toLowerCase() === 'tie') return null;
    if (!statsMap[name]) {
      statsMap[name] = {
        name,
        w: 0,
        l: 0,
        gw: 0,
        gl: 0,
        mvp: 0,
      };
    }
    return statsMap[name];
  };

  (allPlayers || []).forEach((p) => ensurePlayer(p));

  const filterFn = (session) => {
    if (!isRankedSession(session)) return false;
    if (!filter) return true;
    if (typeof filter === 'function') return filter(session);
    if (filter.mode === 'mvp' && !isRotationSession(session)) return false;
    if (filter.mode === 'classic' && !isClassicSession(session)) return false;
    if (filter.sessionIds && Array.isArray(filter.sessionIds)) {
      return filter.sessionIds.includes(session.id);
    }
    if (filter.eventId) {
      const sid = session.eventId || session.seasonId;
      if (sid !== filter.eventId) return false;
    }
    return true;
  };

  (archivedSessions || []).forEach((session) => {
    if (!filterFn(session)) return;

    // 1. Add Game Wins and Game Losses (GW / GL)
    const gameStats = getSessionGameStats(session);
    Object.entries(gameStats).forEach(([name, gs]) => {
      const p = ensurePlayer(name);
      if (p) {
        p.gw += gs.gw || 0;
        p.gl += gs.gl || 0;
      }
    });

    // 2. Add Match Wins, Losses, and MVP awards
    if (isClassicSession(session)) {
      // Completed classic match
      if (isCompletedClassicMatch(session)) {
        const [winners, losers] = getClassicTeams(session);
        winners.forEach((name) => {
          const p = ensurePlayer(name);
          if (p) p.w += 1;
        });
        losers.forEach((name) => {
          const p = ensurePlayer(name);
          if (p) p.l += 1;
        });
      }
      // Classic never awards MVP!
    } else {
      // Rotation session = 1 match
      const outcome = getRotationMatchOutcome(session);
      outcome.winners.forEach((name) => {
        const p = ensurePlayer(name);
        if (p) p.w += 1;
      });
      outcome.losers.forEach((name) => {
        const p = ensurePlayer(name);
        if (p) p.l += 1;
      });
      if (outcome.mvpPlayer) {
        const p = ensurePlayer(outcome.mvpPlayer);
        if (p) p.mvp += 1;
      }
    }
  });

  // Calculate derived metrics & compatibility aliases
  Object.values(statsMap).forEach((st) => {
    const totalMatches = st.w + st.l;
    st.winPct = totalMatches > 0 ? st.w / totalMatches : 0;
    st.gameDiff = st.gw - st.gl;

    // Compatibility aliases
    st.wins = st.w;
    st.losses = st.l;
    st.matchesWon = st.w;
    st.matchesLost = st.l;
    st.gamesWon = st.gw;
    st.gamesLost = st.gl;
    st.gamesPlayed = st.gw + st.gl;
    st.mvpCount = st.mvp;
  });

  return statsMap;
}

/**
 * David's rules comparator for event & overall tables:
 * 1. Match wins (W)
 * 2. Match win % (W / (W + L))
 * 3. Game difference (GW - GL)
 * 4. Name (alphabetical, display order only; players level on 1-3 share the position)
 */
export function comparePlayers(a, b) {
  const wA = a.w ?? a.wins ?? a.matchesWon ?? 0;
  const wB = b.w ?? b.wins ?? b.matchesWon ?? 0;
  if (wB !== wA) return wB - wA;

  const totalA = wA + (a.l ?? a.losses ?? a.matchesLost ?? 0);
  const totalB = wB + (b.l ?? b.losses ?? b.matchesLost ?? 0);
  const winPctA = a.winPct !== undefined ? a.winPct : (totalA > 0 ? wA / totalA : 0);
  const winPctB = b.winPct !== undefined ? b.winPct : (totalB > 0 ? wB / totalB : 0);
  if (winPctB !== winPctA) return winPctB - winPctA;

  const gdA = a.gameDiff !== undefined ? a.gameDiff : ((a.gw ?? a.gamesWon ?? 0) - (a.gl ?? a.gamesLost ?? 0));
  const gdB = b.gameDiff !== undefined ? b.gameDiff : ((b.gw ?? b.gamesWon ?? 0) - (b.gl ?? b.gamesLost ?? 0));
  if (gdB !== gdA) return gdB - gdA;

  // MVPs are a badge of honour only; they never affect ranking.
  const nameA = a.name || a.player || '';
  const nameB = b.name || b.player || '';
  return nameA.localeCompare(nameB);
}

/**
 * Determine an event (season) winner from player stats.
 * Applies the 3 criteria: W -> Win % -> GW - GL. MVPs never decide a winner.
 * If tied across all 3 criteria, returns { player: 'Tie', tiedPlayers: [...] }.
 */
export function getEventWinner(statsMapOrList) {
  const list = Array.isArray(statsMapOrList)
    ? [...statsMapOrList]
    : Object.entries(statsMapOrList || {}).map(([name, s]) => ({ name, ...s }));

  const activePlayers = list.filter((p) => {
    const w = p.w ?? p.wins ?? 0;
    const l = p.l ?? p.losses ?? 0;
    const gw = p.gw ?? p.gamesWon ?? 0;
    const gl = p.gl ?? p.gamesLost ?? 0;
    return w + l > 0 || gw + gl > 0;
  });

  if (activePlayers.length === 0) return null;

  activePlayers.sort(comparePlayers);

  const top = activePlayers[0];
  const topW = top.w ?? top.wins ?? 0;
  const topTotal = topW + (top.l ?? top.losses ?? 0);
  const topWinPct = top.winPct !== undefined ? top.winPct : (topTotal > 0 ? topW / topTotal : 0);
  const topGD = top.gameDiff !== undefined ? top.gameDiff : ((top.gw ?? top.gamesWon ?? 0) - (top.gl ?? top.gamesLost ?? 0));
  const topMvp = top.mvp ?? top.mvpCount ?? 0;

  // Find all players tied across all 3 criteria
  const tiedAtTop = activePlayers.filter((p) => {
    const w = p.w ?? p.wins ?? 0;
    const total = w + (p.l ?? p.losses ?? 0);
    const winPct = p.winPct !== undefined ? p.winPct : (total > 0 ? w / total : 0);
    const gd = p.gameDiff !== undefined ? p.gameDiff : ((p.gw ?? p.gamesWon ?? 0) - (p.gl ?? p.gamesLost ?? 0));
    return w === topW && winPct === topWinPct && gd === topGD;
  });

  if (tiedAtTop.length > 1) {
    return {
      player: 'Tie',
      tiedPlayers: tiedAtTop.map((p) => p.name || p.player),
      w: topW,
      l: top.l ?? top.losses ?? 0,
      gw: top.gw ?? top.gamesWon ?? 0,
      gl: top.gl ?? top.gamesLost ?? 0,
      mvpCount: topMvp,
      winPercentage: topWinPct,
      gameDiff: topGD,
    };
  }

  return {
    player: top.name || top.player,
    w: topW,
    l: top.l ?? top.losses ?? 0,
    gw: top.gw ?? top.gamesWon ?? 0,
    gl: top.gl ?? top.gamesLost ?? 0,
    mvpCount: topMvp,
    winPercentage: topWinPct,
    gameDiff: topGD,
  };
}
